const DEFAULT_ALLOWED_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  's',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul'
]);

const DROP_WITH_CONTENT_TAGS = new Set([
  'button',
  'embed',
  'form',
  'iframe',
  'input',
  'object',
  'script',
  'select',
  'style',
  'textarea'
]);

const GLOBAL_ALLOWED_ATTRIBUTES = new Set(['class', 'title', 'style']);
const TAG_ALLOWED_ATTRIBUTES = {
  a: new Set(['href', 'target', 'rel']),
  img: new Set(['src', 'alt', 'width', 'height']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan']),
  ol: new Set(['start']),
  li: new Set(['value'])
};

const SAFE_STYLE_PROPERTIES = new Set([
  'background-color',
  'color',
  'font-style',
  'font-weight',
  'list-style-type',
  'margin-left',
  'text-align',
  'text-decoration'
]);

const escapeHtml = value =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeStyle = styleValue => {
  const declarations = String(styleValue || '')
    .split(';')
    .map(item => item.trim())
    .filter(Boolean);

  const safeDeclarations = declarations.reduce((result, declaration) => {
    const separatorIndex = declaration.indexOf(':');
    if (separatorIndex <= 0) {
      return result;
    }

    const property = declaration
      .slice(0, separatorIndex)
      .trim()
      .toLowerCase();
    const value = declaration.slice(separatorIndex + 1).trim();

    if (!SAFE_STYLE_PROPERTIES.has(property)) {
      return result;
    }

    if (!value || /expression\s*\(|url\s*\(|javascript:/i.test(value)) {
      return result;
    }

    result.push(`${property}: ${value}`);
    return result;
  }, []);

  return safeDeclarations.join('; ');
};

const normalizeNumericAttribute = value => {
  const numeric = String(value || '').trim();
  return /^\d{1,4}$/.test(numeric) ? numeric : '';
};

const sanitizeUrl = (rawValue, { baseUrl = '', allowDataImages = false } = {}) => {
  const value = String(rawValue || '').trim();
  if (!value) {
    return '';
  }

  if (value.startsWith('#')) {
    return value;
  }

  if (/^data:/i.test(value)) {
    return allowDataImages && /^data:image\/(?:png|jpeg|jpg|gif|webp);/i.test(value) ? value : '';
  }

  if (/^(mailto:|tel:)/i.test(value)) {
    return value;
  }

  try {
    const url = baseUrl ? new URL(value, baseUrl) : new URL(value, window.location.origin);
    return /^(http:|https:)$/i.test(url.protocol) ? url.toString() : '';
  } catch (error) {
    return /^[/.?]/.test(value) ? value : '';
  }
};

const unwrapElement = node => {
  const parent = node.parentNode;
  if (!parent) {
    return;
  }

  while (node.firstChild) {
    parent.insertBefore(node.firstChild, node);
  }

  parent.removeChild(node);
};

const sanitizeElement = (node, options) => {
  const tagName = node.tagName.toLowerCase();

  if (DROP_WITH_CONTENT_TAGS.has(tagName)) {
    node.remove();
    return;
  }

  if (!DEFAULT_ALLOWED_TAGS.has(tagName)) {
    unwrapElement(node);
    return;
  }

  Array.from(node.attributes).forEach(attribute => {
    const name = attribute.name.toLowerCase();
    const value = String(attribute.value || '');
    const allowedAttributes = TAG_ALLOWED_ATTRIBUTES[tagName] || new Set();
    const isAllowed = GLOBAL_ALLOWED_ATTRIBUTES.has(name) || allowedAttributes.has(name);

    if (!isAllowed || name.startsWith('on')) {
      node.removeAttribute(attribute.name);
      return;
    }

    if (name === 'style') {
      const sanitizedStyle = sanitizeStyle(value);
      if (sanitizedStyle) {
        node.setAttribute('style', sanitizedStyle);
      } else {
        node.removeAttribute('style');
      }
      return;
    }

    if (name === 'href' || name === 'src') {
      const sanitizedUrl = sanitizeUrl(value, {
        baseUrl: options.baseUrl,
        allowDataImages: tagName === 'img' && options.allowDataImages
      });

      if (!sanitizedUrl) {
        node.removeAttribute(attribute.name);
      } else {
        node.setAttribute(attribute.name, sanitizedUrl);
      }

      return;
    }

    if (name === 'target') {
      const safeTarget = value === '_blank' ? '_blank' : '';
      if (!safeTarget) {
        node.removeAttribute(attribute.name);
      } else {
        node.setAttribute('target', safeTarget);
        node.setAttribute('rel', 'noopener noreferrer');
      }
      return;
    }

    if (['width', 'height', 'colspan', 'rowspan', 'start', 'value'].includes(name)) {
      const normalized = normalizeNumericAttribute(value);
      if (normalized) {
        node.setAttribute(attribute.name, normalized);
      } else {
        node.removeAttribute(attribute.name);
      }
    }
  });

  if (tagName === 'a' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }

  Array.from(node.childNodes).forEach(child => sanitizeNode(child, options));
};

const sanitizeNode = (node, options) => {
  if (!node) {
    return;
  }

  if (node.nodeType === 3) {
    return;
  }

  if (node.nodeType !== 1) {
    if (node.parentNode) {
      node.parentNode.removeChild(node);
    }
    return;
  }

  sanitizeElement(node, options);
};

const sanitizeHtml = (html, options = {}) => {
  const rawHtml = String(html || '');

  if (!rawHtml) {
    return '';
  }

  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return escapeHtml(rawHtml);
  }

  const parser = new window.DOMParser();
  const documentNode = parser.parseFromString(rawHtml, 'text/html');

  Array.from(documentNode.body.childNodes).forEach(node => sanitizeNode(node, options));

  return documentNode.body.innerHTML;
};

export default sanitizeHtml;
export { sanitizeHtml };
